import { Suspense, useCallback, useContext, useEffect } from "react";
import { useHistory } from "react-router";
import { motion } from "motion/react";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import { useMediaQuery } from "react-responsive";
import useWindowSize from "../../hooks/useWindowSize";
import { OverlayContext } from "../../functional-components/OverlayContext";
import ErrorBoundary from "../../functional-components/ErrorBoundary";
import { ErrorDisplaySmall } from "../ErrorDisplay";
import "./style.scss";
import Icon from "../Icon";
import SimpleLoadingSpinner from "../SimpleLoadingSpinner";
import classNames from "classnames";

export const OverlayCover = ({ children }) => {
  const history = useHistory();

  return (
    <>
      <motion.div
        id="overlay-cover"
        style={{
          top: "var(--padding-top)"
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={e => {
          if (e.target.id === "overlay-cover") {
            //console.log("close overlay - click overlay-cover");
            history.goBack();
          }
          e.stopPropagation();
        }}
      ></motion.div>
      {children}
    </>
  );
};

const GenericOverlay = ({ id, noPadding = false, children }) => {
  const history = useHistory();
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const winDim = useWindowSize();
  const { setOverlayOpened } = useContext(OverlayContext);

  useEffect(() => {
    setOverlayOpened(true);
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = "unset";
      setOverlayOpened(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const docClickHandler = useCallback(
    e => {
      const fullscreenElements = document.getElementsByClassName("fullscreen");
      const modalElements = document.getElementsByClassName("modal");
      if (
        document.getElementById("overlay-content") &&
        !document.getElementById("overlay-content").contains(e.target) &&
        !document.getElementById("overlay-cover").contains(e.target) &&
        !document.getElementById("overlay-close").contains(e.target) &&
        !document.getElementById("overlay-maximize").contains(e.target) &&
        !document.getElementById("top-navigation").contains(e.target) &&
        !document.getElementById("flyout-portal-root").contains(e.target) &&
        !document.getElementById("ui-messages").contains(e.target) &&
        !(fullscreenElements[0] && fullscreenElements[0].contains(e.target)) &&
        !(modalElements[0] && modalElements[0].contains(e.target))
      ) {
        //console.log("close overlay - click document");
        history.goBack();
      }
    },
    [history]
  );

  useEffect(() => {
    setTimeout(() => {
      document.body.addEventListener("click", docClickHandler);
    }, 1);
    return () => {
      document.body.removeEventListener("click", docClickHandler);
    };
  }, [docClickHandler]);

  return (
    <>
      <motion.div
        id={id ? id : "overlay-window"}
        className={classNames("overlay-window", { "no-padding": noPadding })}
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        exit={{ opacity: 0, scale: 0.7 }}
        transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
        style={{
          ...(isSmall
            ? {
                top: "var(--padding-top)",
                bottom: 0,
                left: 0,
                right: 0
              }
            : {
                top: "calc(16px + var(--padding-top))",
                bottom: 16,
                left:
                  winDim.innerWidth > 1400 ? winDim.innerWidth / 2 - 650 : 16,
                right:
                  winDim.innerWidth > 1400 ? winDim.innerWidth / 2 - 650 : 16
              }),
          borderRadius: isSmall ? 0 : "0.25rem"
        }}
      >
        <div className="overlay-content">
          <ErrorBoundary
            boundaryId={"overlay-" + (id ? id : "unspecified")}
            fallback={<ErrorDisplaySmall />}
          >
            <Suspense fallback={<SimpleLoadingSpinner />}>{children}</Suspense>
          </ErrorBoundary>
          <KeyboardEventHandler
            handleKeys={["esc"]}
            handleFocusableElements
            onKeyEvent={(key, e) => {
              switch (key) {
                case "esc":
                  history.goBack();
                  e.preventDefault();
                  break;
                default:
              }
            }}
          />
        </div>
        <div
          className="overlay-button-bar"
          style={{
            ...(isSmall
              ? {
                  top: 20,
                  right: 24
                }
              : {
                  top: 28,
                  right: 38
                })
          }}
        >
          <button
            className={classNames("overlay-button", {
              "no-padding": noPadding
            })}
            id={"overlay-close"}
            onClick={() => {
              history.goBack();
            }}
          >
            <Icon name="xmark" />
          </button>
          {!isSmall && (
            <button
              className={classNames("overlay-button", {
                "no-padding": noPadding
              })}
              id={"overlay-maximize"}
              onClick={() => {
                history.replace(history.location.pathname, {
                  ...history.location.state,
                  noOverlay: true
                });
              }}
            >
              <Icon name="expand" />
            </button>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default GenericOverlay;
